// "Start a conversation" (ios/Sources/Views/NewConversationSheet.swift): pick one person for a DM
// — yourself included, Mattermost's personal space — or up to seven for a group message. The
// endpoint follows from the count (shared/API.md §19.2), decided in PeoplePickerPresentation and
// the worker's `openDirectChannel`, never left to the server.
//
// The second tab lists the conversations the reader CLOSED (§29): one click reopens the same
// channel (the preference flips back) and lands inside it. Opening a closed person from the
// People tab brings the same channel back too — never a second conversation.
import { useMemo, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import { ClosedConversationsPresentation } from '../../../core/closedConversationsPresentation';
import { PeoplePickerPresentation } from '../../../core/peoplePickerPresentation';
import type { UserRecord } from '../../../core/records';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { useLive } from '../../hooks';
import { Button, Dialog, EmptyState, SearchInput, Segmented } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';
import { useFocusOnMount, useStableClose } from './dialogFocus';
import { PeoplePicker } from './PeoplePicker';
import './people.css';

type Mode = 'people' | 'closed';

export function NewConversationDialog({ onClose: onCloseProp }: { onClose(): void }) {
  const onClose = useStableClose(onCloseProp);
  const shell = useShell();
  const root = useRef<HTMLDivElement>(null);
  const meId = shell.me.id;
  const [mode, setMode] = useState<Mode>('people');
  const [selected, setSelected] = useState<string[]>([]);
  const known = useRef(new Map<string, UserRecord>()).current;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ceiling, setCeiling] = useState(false);

  const closed = useClosedConversations(shell.team.id);
  useFocusOnMount(root, '.people-sticky input', mode);

  const finish = (channelId: string) => {
    onClose();
    shell.openChannel(channelId);
  };

  const open = async () => {
    if (busy || !selected.length) return;
    setBusy(true);
    setError(null);
    try {
      const result = await data.call('openDirectChannel', selected);
      if ('channelId' in result) finish(result.channelId);
      else setError(result.error === 'deactivated' ? t('web.people.deactivatedRefused') : t('people.openFailed'));
    } catch {
      setError(t('people.openFailed'));
    } finally {
      setBusy(false);
    }
  };

  const reopen = async (channelId: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await data.call('reopenConversation', channelId);
      if ('channelId' in result) finish(result.channelId);
      else setError(t('people.openFailed'));
    } catch {
      setError(t('people.openFailed'));
    } finally {
      setBusy(false);
    }
  };

  const changeSelection = (next: string[]) => {
    setSelected(next);
    setCeiling(false);
    setError(null);
  };

  const target = PeoplePickerPresentation.target(selected.length);
  let hint = t('web.people.pickHint');
  if (target === 'direct') hint = selected[0] === meId ? t('web.people.targetSelf') : t('web.people.targetDirect');
  else if (target === 'group') hint = tf('web.people.targetGroup', selected.length);
  if (ceiling) hint = t('web.people.limitReached');

  const footer = (
    <>
      <span className={`people-footer-hint${error ? ' error' : ''}`} role={error ? 'alert' : undefined}>
        {error ?? (mode === 'people' ? hint : t('web.people.closedHint'))}
      </span>
      {mode === 'people' ? (
        <Button kind="primary" onClick={() => void open()} disabled={!selected.length || busy}>
          {t('people.open')}
        </Button>
      ) : null}
    </>
  );

  return (
    <Dialog title={t('people.newConversation')} onClose={onClose} footer={footer} width={520}>
      <div className="people-dialog-root" ref={root}>
        <div className="people-tabs" style={{ padding: '4px 0 4px' }}>
          <Segmented<Mode>
            value={mode}
            onChange={(m) => {
              setMode(m);
              setError(null);
            }}
            options={[
              { value: 'people', label: t('people.tabPeople') },
              { value: 'closed', label: t('people.tabClosed'), count: closed.rows.length || undefined },
            ]}
          />
        </div>
        {mode === 'people' ? (
          <PeoplePicker
            teamId={shell.team.id}
            meId={meId}
            selected={selected}
            onSelectedChange={changeSelection}
            known={known}
            onSubmit={() => void open()}
            onCeiling={() => setCeiling(true)}
          />
        ) : (
          <ClosedList rows={closed.rows} loaded={closed.loaded} busy={busy} onReopen={(id) => void reopen(id)} />
        )}
      </div>
    </Dialog>
  );
}

/** The closed D/G conversations of the open team, store-first (no request to discover them). */
function useClosedConversations(teamId: string) {
  const { value } = useLive(() => data.call('closedConversations', teamId), ['channels', 'categories', 'users', 'channelParticipants'], [teamId]);
  const rows = useMemo(() => {
    if (!value || !value.categoriesKnown) return [];
    return ClosedConversationsPresentation.rows({
      conversations: value.conversations,
      visibleChannelIds: new Set(value.visibleChannelIds),
      currentUserId: value.currentUserId,
      usersById: new Map(value.users.map((u) => [u.id, u])),
      participantsByChannelId: new Map(Object.entries(value.participants)),
      unknownUserLabel: t('channelList.unknownUser'),
      unknownGroupLabel: t('channelList.unknownGroup'),
    });
  }, [value]);
  return { rows, loaded: value !== undefined };
}

function ClosedList({ rows, loaded, busy, onReopen }: { rows: ReturnType<typeof ClosedConversationsPresentation.rows>; loaded: boolean; busy: boolean; onReopen(channelId: string): void }) {
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const visible = ClosedConversationsPresentation.filter(rows, query);
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!visible.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight((h) => (h + step + visible.length) % visible.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = visible[Math.min(highlight, visible.length - 1)];
      if (row) onReopen(row.channelId);
    }
  };
  if (loaded && !rows.length) return <EmptyState title={t('people.closedEmpty')} />;
  return (
    <>
      <div className="people-sticky" onKeyDown={onKeyDown}>
        <SearchInput
          value={query}
          onChange={(v) => {
            setQuery(v);
            setHighlight(0);
          }}
          placeholder={t('web.people.closedFilter')}
          autoFocus
        />
      </div>
      {!visible.length && query.trim() ? <EmptyState title={tf('web.people.noMatches', query.trim())} /> : null}
      <div className="people-list" role="listbox" aria-label={t('people.tabClosed')}>
        {visible.map((row, index) => (
          <button
            key={row.channelId}
            className={`list-row clickable people-row${index === highlight ? ' highlighted' : ''}`}
            onMouseMove={() => index !== highlight && setHighlight(index)}
            onClick={() => onReopen(row.channelId)}
            disabled={busy}
            role="option"
            aria-selected={index === highlight}
          >
            <span className="list-row-leading">
              {row.channelType === 'D' && row.avatarUserId ? (
                <Avatar userId={row.avatarUserId} name={row.name} size={32} version={row.avatarLastPictureUpdate} />
              ) : (
                <span className="people-glyph">
                  <Icon name={row.channelType === 'G' ? 'people' : 'person'} size={17} />
                </span>
              )}
            </span>
            <span className="list-row-text">
              <span className="list-row-title">{row.name}</span>
              <span className="list-row-subtitle">
                {row.detail.kind === 'direct' ? (row.detail.username ? '@' + row.detail.username : '') : tf('people.groupCount', row.detail.participantCount)}
              </span>
            </span>
            <span className="list-row-trailing">
              <span className="people-trailing-action">{t('people.reopen')}</span>
            </span>
          </button>
        ))}
      </div>
    </>
  );
}
